import {
  HttpErrorResponse,
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { endsAtFrom, isMaintenanceBody } from './service-availability.model';
import { ServiceAvailabilityStore } from './service-availability.store';

/**
 * Watches every API call and tells the store what it learned.
 *
 * Passive by design: it classifies and forwards, then re-throws untouched. Nothing here swallows
 * a response or rewrites an error, so every component keeps its own error handling — this only
 * decides whether there is something wrong with the platform as a whole.
 *
 * Placed so that it sees outcomes the individual call sites do not care about. A bookings page
 * that shows "failed to load" is right to move on; it is the fact that the last two calls from
 * anywhere in the app both failed to reach a server that means something.
 */
export const serviceAvailabilityInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const store = inject(ServiceAvailabilityStore);

  // Only the API can tell us anything about the API.
  //
  // Load-bearing, not an optimisation: this app serves its own static files from its own host,
  // which stays up when the backend does not. The bundled i18n fallback is fetched from there the
  // moment the backend translation call fails, and counting that success would reset the failure
  // count and hide the very outage that triggered it.
  if (!req.url.startsWith(environment.apiUrl)) return next(req);

  return next(req).pipe(
    // Filtered to the response event, and that is load-bearing. An interceptor sees the whole
    // event stream — HttpClient only narrows it to the body further down the chain — so an
    // unfiltered tap fires on HttpSentEvent the instant the request LEAVES, before anything has
    // come back. That would reset the failure counter on every outgoing call and a real outage
    // could never reach the threshold: the block would never appear at all.
    tap(event => {
      if (event.type === HttpEventType.Response) store.reportReachable();
    }),
    catchError((error: unknown) => {
      classify(error, store);
      return throwError(() => error);
    })
  );
};

function classify(error: unknown, store: ServiceAvailabilityStore): void {
  if (!(error instanceof HttpErrorResponse)) return;

  // Angular reports "never got an answer" as status 0 — a dead server, a dead network, a CORS
  // preflight that no longer reaches anything. All of them mean nothing answered.
  if (error.status === 0) {
    store.reportTransportFailure();
    return;
  }

  if (error.status === 503 && isMaintenanceBody(error.error)) {
    store.reportMaintenance(endsAtFrom(error.error));
    return;
  }

  // A gateway error means something answered, but not the app: the instance is restarting, being
  // swapped, or out of the load balancer's rotation. Counted with the transport failures rather
  // than as a reachable backend, because from the user's side it is the same outage.
  //
  // A bare 503 lands here too. That is deliberate — without our error code it may be Azure's, and
  // guessing "maintenance" from the status alone would put a reassuring, specific screen in front
  // of what could be a real fault.
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    store.reportTransportFailure();
    return;
  }

  // Everything else — 4xx, 500 — is the application answering. Unwelcome, but reachable.
  store.reportReachable();
}
